import { useContext, useMemo, useState } from "react";
import {
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { callEndpoint, doFormalize } from "../../lib/functions";
import { categoryToIcon } from "../../lib/definitions";
import { categoryToColor } from "../../lib/lib";
import { useScrollToTop } from "../../lib/hooks/useScrollToTop";
import { MenuItem } from "@mui/material";
import { ConfirmModal } from "../elements/confirmModal";
import { MButton } from "../elements/mbutton";
import { MModal } from "../elements/mmodal";
import { DataContext } from "../../lib/contexts";
import { MInput } from "../elements/mInputs";
import { MSelect } from "../elements/mselect";
import { OrganicCheckbox } from "../elements/organicCheckbox";
import { ItemOption } from "../shopping/itemOption";
import {
  DeleteItemEndpoint,
  Item,
  ItemCategory,
  UpdateItem,
} from "@ckal-software/ckal-lib/dist/apps/madplanen";
import { useNotifcation } from "../../lib/hooks/useNotification";

export function AdminContent() {
  const [searchValue, setSearchValue] = useState("");
  const searchResultsRef = useScrollToTop(searchValue);
  const [selectedItem, setSelectedItem] = useState<Item>();
  const [renameValue, setRenameValue] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<ItemCategory>();
  const [isUpdatingItem, setIsUpdatingItem] = useState(false);
  const [isDeletingItem, setIsDeletingItem] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isOrganic, setIsOrganic] = useState(false);

  const { items, refreshItems } = useContext(DataContext);

  const notify = useNotifcation();

  const filteredItems = useMemo(() => {
    const normalizedQuery = doFormalize(searchValue);

    return items
      .filter((item) => doFormalize(item.name).includes(normalizedQuery))
      .sort((leftItem, rightItem) => {
        const leftName = doFormalize(leftItem.name);
        const rightName = doFormalize(rightItem.name);
        const leftRank =
          leftName === normalizedQuery
            ? 0
            : leftName.startsWith(normalizedQuery)
              ? 1
              : 2;
        const rightRank =
          rightName === normalizedQuery
            ? 0
            : rightName.startsWith(normalizedQuery)
              ? 1
              : 2;

        if (leftRank !== rightRank) {
          return leftRank - rightRank;
        }

        return leftName.localeCompare(rightName);
      });
  }, [items, searchValue]);

  const visibleItems = filteredItems.slice(0, 30);
  const remainingItemCount = filteredItems.length - visibleItems.length;

  return (
    <div>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}
      >
        <MInput
          prefix={<SearchOutlined style={{ color: "#9ca3af", fontSize: 18 }} />}
          size="large"
          placeholder="Søg vare..."
          allowClear
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="new-item-input"
        />
      </div>
      <div
        ref={searchResultsRef}
        style={{
          height: "224px",
          overflow: "auto",
          padding: "4px 0",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
        }}
      >
        {visibleItems.map((item) => (
          <ItemOption
            key={item._id}
            name={item.name}
            category={item.category}
            isOrganic={item.isOrganic}
            inlineEcoLabel
            small
            style={{ margin: "0 4px", padding: "6px 8px" }}
            onClick={() => {
              setRenameValue(item.name);
              setSelectedCategory(item.category);
              setIsOrganic(!!item.isOrganic);
              setSelectedItem(item);
            }}
          />
        ))}
        {remainingItemCount > 0 && (
          <div
            style={{
              padding: "4px 12px 12px",
              color: "#9ca3af",
              fontSize: 13,
              textAlign: "center",
            }}
          >
            ... og {remainingItemCount} flere
          </div>
        )}
      </div>
      {selectedItem && (
        <MModal
          visible={!!selectedItem && !showDeleteConfirm}
          onClose={() => setSelectedItem(undefined)}
          title="Rediger vare"
          hideCloseIcon
          content={
            <div>
              <MInput
                size="large"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
              />
              <MSelect
                style={{ marginTop: 16 }}
                value={selectedCategory || ""}
                onChange={(e) =>
                  setSelectedCategory(e.target.value as ItemCategory)
                }
                size="large"
              >
                {Object.values(ItemCategory)
                  .filter((cat) => !["Købte", ItemCategory.Other].includes(cat))
                  .map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 30,
                            height: 30,
                            flexShrink: 0,
                            borderRadius: 8,
                            background: `color-mix(in srgb, ${categoryToColor[cat]} 15%, transparent)`,
                          }}
                        >
                          {categoryToIcon[cat]}
                        </span>
                        <span>{cat}</span>
                      </span>
                    </MenuItem>
                  ))}
              </MSelect>
              <div
                style={{
                  marginTop: "16px",
                }}
              >
                <OrganicCheckbox checked={isOrganic} onChange={setIsOrganic} />
              </div>
            </div>
          }
          footer={
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                justifyContent: "space-between",
              }}
            >
              <MButton type="ghost" onClick={() => setSelectedItem(undefined)}>
                Annuller
              </MButton>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  columnGap: "8px",
                  marginLeft: "auto",
                }}
              >
                <MButton
                  type="danger"
                  icon={<DeleteOutlined />}
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Slet
                </MButton>
                <MButton
                  type="primary"
                  icon={<EditOutlined />}
                  disabled={!renameValue.trim() || !selectedCategory}
                  loading={isUpdatingItem}
                  onClick={async () => {
                    if (!selectedCategory) {
                      return;
                    }

                    const [err] = await callEndpoint(
                      UpdateItem,
                      {
                        id: selectedItem._id,
                        update: {
                          name: renameValue.trim(),
                          category: selectedCategory,
                          isOrganic,
                        },
                      },
                      { setWorking: setIsUpdatingItem },
                    );

                    if (err) {
                      notify(`Opdatering af vare: ${err.error}`);
                      return;
                    }

                    await refreshItems();
                    setSelectedItem(undefined);
                  }}
                >
                  Opdater
                </MButton>
              </div>
            </div>
          }
        />
      )}
      {selectedItem && (
        <ConfirmModal
          visible={showDeleteConfirm}
          title="Slet vare"
          message={
            <>
              Vil du slette <strong>{selectedItem.name}</strong>?
            </>
          }
          onCancel={() => setShowDeleteConfirm(false)}
          confirmButton={
            <MButton
              type="danger"
              icon={<DeleteOutlined />}
              loading={isDeletingItem}
              onClick={async () => {
                const [err] = await callEndpoint(
                  DeleteItemEndpoint,
                  { id: selectedItem._id },
                  { setWorking: setIsDeletingItem },
                );

                if (err) {
                  notify(`Sletning af vare: ${err.error}`);
                  return;
                }

                await refreshItems();
                setShowDeleteConfirm(false);
                setSelectedItem(undefined);
              }}
            >
              Slet
            </MButton>
          }
        />
      )}
    </div>
  );
}
